import { Check, Copy } from 'lucide-react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { initials } from '../../../shared/text';
import { useCopy } from '../../lib/hooks';
import { cx } from './cx';
import styles from './Display.module.css';

// ---------- status & labels

export type Tone = 'success' | 'muted' | 'warning';

export function StatusDot({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className={cx(styles.status, tone === 'muted' ? styles.statusMuted : undefined)}>
      <span className={cx(styles.dot, styles[`dot_${tone}`])} />
      {children}
    </span>
  );
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'muted' | 'blue';

export function Badge({ tone = 'neutral', dot, children, size = 'md' }: { tone?: BadgeTone; dot?: boolean; children: ReactNode; size?: 'sm' | 'md' }) {
  return (
    <span className={cx(styles.badge, styles[`badge_${tone}`], size === 'sm' ? styles.badgeSm : undefined)}>
      {dot ? <span className={styles.badgeDot} /> : null}
      {children}
    </span>
  );
}

export function CodeTag({ children, size = 'sm' }: { children: ReactNode; size?: 'sm' | 'md' }) {
  return <span className={cx(styles.code, size === 'md' ? styles.codeMd : undefined)}>{children}</span>;
}

/** Mono pill with a URL; click copies and briefly shows «Скопировано». */
export function CopyChip({ label, value }: { label: string; value: string }) {
  const { copied, copy } = useCopy();
  return (
    <button type="button" className={styles.copyChip} onClick={() => copy('chip', value)} title="Скопировать ссылку">
      <span className={styles.copyText}>{label}</span>
      {copied ? (
        <span className={styles.copied}>
          <Check size={14} strokeWidth={2} />
          Скопировано
        </span>
      ) : (
        <Copy size={14} strokeWidth={1.6} />
      )}
    </button>
  );
}

export function Avatar({ name, size = 32, tone = 'soft' }: { name: string; size?: number; tone?: 'soft' | 'grey' }) {
  return (
    <span
      className={cx(styles.avatar, tone === 'grey' ? styles.avatarGrey : undefined)}
      style={{ width: size, height: size, fontSize: size >= 32 ? 12 : 11 }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

/** Black rounded square with the academy initial. */
export function AcademyMark({ name, size = 20 }: { name: string; size?: 20 | 26 | 28 | 40 }) {
  const font = { 20: 11, 26: 13, 28: 14, 40: 17 }[size];
  const radius = size >= 40 ? 12 : size >= 26 ? 8 : 6;
  return (
    <span className={styles.academyMark} style={{ width: size, height: size, fontSize: font, borderRadius: radius }} aria-hidden>
      {name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

// ---------- empty, loading, error

export interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  text?: ReactNode;
  actions?: ReactNode;
  minHeight?: number;
}

export function EmptyState({ icon, title, text, actions, minHeight = 480 }: EmptyStateProps) {
  return (
    <div className={styles.empty} style={{ minHeight }}>
      <div className={styles.emptyInner}>
        <span className={styles.emptyIcon}>{icon}</span>
        <h3 className={styles.emptyTitle}>{title}</h3>
        {text ? <p className={styles.emptyText}>{text}</p> : null}
        {actions ? <div className={styles.emptyActions}>{actions}</div> : null}
      </div>
    </div>
  );
}

export function Spinner({ size = 20 }: { size?: number }) {
  return <span className={styles.spinner} style={{ width: size, height: size }} role="status" aria-label="Загрузка" />;
}

export function PageLoader({ minHeight = 320 }: { minHeight?: number | string }) {
  return (
    <div className={styles.loader} style={{ minHeight }}>
      <Spinner size={22} />
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className={styles.errorState} role="alert">
      <p>{message}</p>
      {onRetry ? (
        <button type="button" className={styles.retry} onClick={onRetry}>
          Повторить
        </button>
      ) : null}
    </div>
  );
}

// ---------- tables

export interface TableProps {
  /** CSS grid-template-columns shared by the head and rows. */
  columns: string;
  children: ReactNode;
  className?: string;
}

export function Table({ columns, children, className }: TableProps) {
  return (
    <div className={cx(styles.table, className)} style={{ '--columns': columns } as CSSProperties} role="table">
      {children}
    </div>
  );
}

export function TableHead({ children }: { children: ReactNode }) {
  return (
    <div className={styles.thead} role="row">
      {children}
    </div>
  );
}

export interface TableRowProps {
  children: ReactNode;
  onOpen?: () => void;
  height?: number;
  label?: string;
}

/** Clickable rows are keyboard-reachable and open on Enter. */
export function TableRow({ children, onOpen, height = 60, label }: TableRowProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onOpen();
    }
  };
  return (
    <div
      className={cx(styles.row, onOpen ? styles.rowClickable : undefined)}
      style={{ minHeight: height }}
      role="row"
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      aria-label={label}
    >
      {children}
    </div>
  );
}

export function TableNote({ children }: { children: ReactNode }) {
  return <div className={styles.tableNote}>{children}</div>;
}

// ---------- brand

export interface LogoMarkProps {
  size?: number;
  leaf?: string;
  grain?: string;
  gap?: string;
  strokeWidth?: number;
}

/** «Росток»: black grain and a blue leaf. */
export function LogoMark({ size = 28, leaf = '#2563eb', grain = '#0a0a0b', gap = '#ffffff', strokeWidth = 4.5 }: LogoMarkProps) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} style={{ flex: 'none' }} aria-hidden>
      <circle cx="23" cy="42" r="15" fill={grain} />
      <path fill={leaf} stroke={gap} strokeWidth={strokeWidth} strokeLinejoin="round" paintOrder="stroke" d="M30 34C46 33 56 23 57 7C41 8 31 18 30 34Z" />
    </svg>
  );
}

export function Logo({ size = 28, background = '#ffffff', wordSize = 18 }: { size?: number; background?: string; wordSize?: number }) {
  return (
    <span className={styles.logo} aria-label="zerno education">
      <LogoMark size={size} gap={background} />
      <span className={styles.logoWords}>
        <span className={styles.logoWord} style={{ fontSize: wordSize }}>
          zerno
        </span>
        <span className={styles.logoSub}>education</span>
      </span>
    </span>
  );
}

/** Blue rounded tile with the white mark, used above auth forms and empty home. */
export function LogoTile({ size = 40 }: { size?: number }) {
  return (
    <span className={styles.logoTile} style={{ width: size, height: size, borderRadius: Math.round(size * 0.3) }}>
      <LogoMark size={Math.round(size * 0.7)} leaf="#ffffff" grain="rgba(255,255,255,0.55)" gap="#2563eb" strokeWidth={4} />
    </span>
  );
}

export function YoutubeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
      <path d="m10 15 5-3-5-3z" />
    </svg>
  );
}
